import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface WorkflowWebhookSecretResponse {
  secret: string;
}

/**
 * Query keys for per-workflow webhook secret
 */
export const workflowWebhookSecretKeys = {
  all: ["workflow-webhook-secret"] as const,
  byWorkflow: (workflowId: string) => [...workflowWebhookSecretKeys.all, workflowId] as const,
};

/**
 * Hook to fetch the per-workflow webhook secret.
 * The backend auto-creates a secret on the first GET request if none exists.
 *
 * @param workflowId - The workflow ID
 * @returns Object with secret, isLoading, error, and refetch
 */
export function useWorkflowWebhookSecret(workflowId: string | undefined) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: workflowWebhookSecretKeys.byWorkflow(workflowId ?? ""),
    queryFn: async (): Promise<WorkflowWebhookSecretResponse> => {
      if (!workflowId) {
        throw new Error("Workflow ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetchJson<{ data: WorkflowWebhookSecretResponse }>(
        `${V2_ENDPOINT}/v1/workflows/${workflowId}/webhook-secret`,
      );

      return response.data;
    },
    enabled: !!workflowId,
    staleTime: 5 * 60 * 1000, // 5 minutes - secrets don't change often
  });

  return {
    data,
    secret: data?.secret ?? null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to regenerate the per-workflow webhook secret.
 * Invalidates the query cache on success so the UI picks up the new secret.
 *
 * @param workflowId - The workflow ID
 */
export function useRegenerateWebhookSecret(workflowId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<WorkflowWebhookSecretResponse> => {
      if (!workflowId) {
        throw new Error("Workflow ID is required");
      }

      if (!V2_ENDPOINT) {
        throw new Error("V2_ENDPOINT not configured");
      }

      const response = await authenticatedFetchJson<{ data: WorkflowWebhookSecretResponse }>(
        `${V2_ENDPOINT}/v1/workflows/${workflowId}/webhook-secret/regenerate`,
        { method: "POST" },
      );

      return response.data;
    },
    onSuccess: () => {
      if (workflowId) {
        queryClient.invalidateQueries({
          queryKey: workflowWebhookSecretKeys.byWorkflow(workflowId),
        });
      }
    },
  });
}
